/**
 * Shared decoration for every flowbite DateRangePicker on the site.
 *
 * Below md the picker is docked to the bottom of the screen as a sheet (see
 * global.scss). A sheet needs to say what it is for and give a way out, and
 * flowbite's own chrome does neither: it renders a month name and two arrows
 * and nothing else. Without a title the guest cannot tell whether the tap they
 * are about to make sets the check-in or the check-out, which matters because
 * the same sheet is used for both ends of the range.
 *
 * The header is injected into the picker element rather than rendered
 * alongside it so it scrolls, animates and stacks with the sheet as one thing.
 * flowbite builds `.datepicker-picker` once and afterwards only updates the
 * text inside it, so a prepended node survives every re-render.
 */

const SIDE_LABELS = ['Select check-in date', 'Select check-out date'];

const registered = new Set();
let outsideHandlerBound = false;

const rootOf = (datePicker) => datePicker.picker && datePicker.picker.element;
const isVisible = (el) => !!el && getComputedStyle(el).display !== 'none';

const isOpen = (rangePicker) =>
  rangePicker.datepickers.some((datePicker) => isVisible(rootOf(datePicker)));

/**
 * A tap outside an open calendar closes the calendar, and nothing else.
 *
 * The inputs behind these pickers are readonly and, on the property page,
 * opacity-0, so a touch elsewhere never blurs them and flowbite's own
 * focusout dismissal never fires. Worse, the picker is appended to <body>, so
 * on the properties archive a tap meant to dismiss it landed outside the
 * filters modal's panel and Alpine's `@click.outside` tore the whole filters
 * popup down with it.
 *
 * Bound once in the capture phase at the document, so it runs before Alpine's
 * own document listener and can stop the event there. Swallowing the
 * dismissing tap is the behaviour a sheet is expected to have: the first tap
 * puts it away, the second acts.
 */
function bindOutsideDismissal() {
  if (outsideHandlerBound) {
    return;
  }
  outsideHandlerBound = true;

  document.addEventListener(
    'click',
    (e) => {
      const open = [...registered].filter(isOpen);

      if (!open.length || !e.target || !e.target.closest) {
        return;
      }

      // Inside a calendar: let it act.
      if (e.target.closest('.datepicker')) {
        return;
      }

      // On the fields that own the calendar: let flowbite switch sides.
      if (open.some((rangePicker) => rangePicker.element && rangePicker.element.contains(e.target))) {
        return;
      }

      open.forEach((rangePicker) => rangePicker.datepickers.forEach((dp) => dp.hide()));
      e.preventDefault();
      e.stopPropagation();
    },
    true,
  );
}

/**
 * Open the check-out calendar on the month the guest just picked for check-in.
 *
 * flowbite resets a picker's view date to `defaultViewDate` (today) every time
 * it hides, and `onChangeDate` in DateRangePicker calls `picker.update()` on
 * both sides after every pick, which does the same. So with a one-sided range
 * the check-out calendar always reopened on the current month, and a guest
 * booking for December had to page forward four months a second time.
 *
 * Hooked to the picker's own `show` event rather than the handover in the
 * template, so it also applies when the guest taps the Check Out field
 * directly. Only moves the view while check-out is still empty; once both ends
 * are set the library's own view date is the right one.
 */
function syncCheckOutMonth(rangePicker) {
  const [startPicker, endPicker] = rangePicker.datepickers;

  if (!startPicker || !endPicker) {
    return;
  }

  endPicker.element.addEventListener('show', () => {
    const checkIn = startPicker.dates[0];

    if (checkIn === undefined || endPicker.dates.length) {
      return;
    }

    endPicker.picker.changeFocus(checkIn).render();
  });
}

export function decorateCalendar(rangePicker) {
  if (!rangePicker || !rangePicker.datepickers) {
    return;
  }

  registered.add(rangePicker);
  bindOutsideDismissal();
  syncCheckOutMonth(rangePicker);

  rangePicker.datepickers.forEach((datePicker, side) => {
    // `picker.element` is the `.datepicker` dropdown root, not the white panel
    // inside it. Appending to the root put the header above the panel, sitting
    // on the page behind with no background of its own.
    const root = datePicker.picker && datePicker.picker.element;
    const panel = root
      ? (root.classList.contains('datepicker-picker') ? root : root.querySelector('.datepicker-picker'))
      : null;

    if (!panel || panel.querySelector('.bv-cal-head')) {
      return;
    }

    const head = document.createElement('div');
    head.className = 'bv-cal-head';

    const title = document.createElement('span');
    title.className = 'bv-cal-head__title';
    title.textContent = SIDE_LABELS[side] || SIDE_LABELS[0];

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'bv-cal-head__close';
    close.setAttribute('aria-label', 'Close the calendar');
    close.innerHTML = '&times;';
    close.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      rangePicker.datepickers.forEach((dp) => dp.hide());
    });

    head.appendChild(title);
    head.appendChild(close);
    panel.insertBefore(head, panel.firstChild);
  });
}

window.bvDecorateCalendar = decorateCalendar;

export default decorateCalendar;
